/**
 * Tile Renderer — fetches its own data and dispatches to the right tile component.
 * One <TileRender boardId tile /> per tile.
 */
import React, { useEffect, useState, useRef } from 'react';
import TileShell from './TileShell';
import TileErrorBoundary from './TileErrorBoundary';
import BigNumber from './BigNumber';
import LineChart from './LineChart';
import BreakdownBar from './BreakdownBar';
import FunnelTile from './FunnelTile';
import KpiTable from './KpiTable';
import SegmentLeaderboard from './SegmentLeaderboard';
import ChannelReachability from './ChannelReachability';
import FlowTile from './FlowTile';
import HeatmapTile from './HeatmapTile';
import { boardsApi } from '../api';

const REGISTRY = {
  big_number:           BigNumber,
  time_series:          LineChart,
  breakdown:            BreakdownBar,
  distribution:         BreakdownBar,
  funnel:               FunnelTile,
  kpi_table:            KpiTable,
  segment_leaderboard:  SegmentLeaderboard,
  channel_reachability: ChannelReachability,
  flow:                 FlowTile,
  heatmap:              HeatmapTile,
  profile_breakdown:    BreakdownBar,   // contacts donut (chart='doughnut', rows[{label,v}])
};

export default function TileRender({ boardId, tile, cohort, editMode, onEdit, onDuplicate, onDelete }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    boardsApi.renderTile(boardId, tile._id || tile.id)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data;
        if (!res?.success || !payload) {
          setState({ loading: false, error: { message: 'Empty response' }, data: null });
          return;
        }
        if (payload.error) {
          setState({ loading: false, error: payload.error, data: null });
          return;
        }
        setState({ loading: false, error: null, data: payload.data });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ loading: false, error: { message: err?.message || 'Render failed' }, data: null });
      });
    return () => { cancelled = true; mounted.current = false; };
  }, [boardId, tile._id, tile.id, tile.viz_config, tile.type, tile.source]);

  const Component = REGISTRY[tile.type];
  return (
    <TileShell tile={tile} loading={state.loading} error={state.error} cohort={cohort}
      editMode={editMode} onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete}>
      <TileErrorBoundary tileTitle={tile?.title}>
        {Component ? <Component data={state.data} tile={tile} /> :
          <div style={{ fontSize: 12, color: '#6b7280' }}>Card type "{tile.type}" not yet supported.</div>}
      </TileErrorBoundary>
    </TileShell>
  );
}
